import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'
import { wishlistApi } from './wishlistApi'

export interface CigarDetailResponse {
  id: string
  name: string
  description?: string
  length?: number
  ringGauge?: number
  shape?: string
  binder?: string
  filler?: string
  wrapper?: string
  strengthLevel?: string
  flavorProfile?: any
  tastingNotes?: string
  pairings?: string[]
  manufacturer?: string
  country?: string
  region?: string
  factory?: string
  images?: string[]
  availability?: string
  priceRange?: string
  averagePrice?: number
  brand?: {
    id: string
    name: string
    country?: string
    logoUrl?: string
    foundedYear?: number
  }
  cigarLine?: {
    id: string
    name: string
    description?: string
    strengthLevel?: string
    wrapperType?: string
    binderType?: string
    fillerType?: string
  }
  vitola?: {
    id: string
    name: string
    description?: string
    length?: number
    ringGauge?: number
  }
  stats?: {
    totalReviews: number
    averageRating: number
    totalWishlist: number
    totalSmoked: number
    strengthDistribution?: any
  }
  reviews?: CigarReview[]
  ratingDistribution?: RatingDistribution[]
  similarCigars?: SimilarCigar[]
}

export interface CigarReview {
  id: string
  overallRating: number
  drawRating?: number
  burnRating?: number
  flavorRating?: number
  appearanceRating?: number
  valueRating?: number
  reviewText: string
  wouldBuyAgain: boolean
  helpfulVotes: number
  createdAt: string
  user: {
    username: string
    firstName?: string
  }
}

export interface RatingDistribution {
  rating: number
  count: number
}

export interface SimilarCigar {
  id: string
  name: string
  brand: {
    id: string
    name: string
    logoUrl?: string
  }
  cigarLine: {
    id: string
    name: string
  }
  vitola: {
    id: string
    name: string
  }
  stats: {
    averageRating: number
    totalReviews: number
  }
}

export interface CigarImagesResponse {
  images: string[]
  imageCount: number
}

export interface AddToWishlistRequest {
  cigarId: string
}

export interface AddToCollectionRequest {
  cigarId: string
  notes?: string
}

export interface CigarSaveStatus {
  inWishlist: boolean
  inCollection: boolean
  wishlistItemId: string | null
  collectionItemId: string | null
}

export const cigarDetailApi = createApi({
  reducerPath: 'cigarDetailApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/database',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['CigarDetail', 'CigarImages', 'Wishlist', 'Collection', 'CigarStatus'],
  endpoints: (builder) => ({
    getCigarDetail: builder.query<CigarDetailResponse, string>({
      query: (id) => `/cigars/${id}`,
      transformResponse: (response: { success: boolean; data: { cigar: CigarDetailResponse } }) => response.data.cigar,
      providesTags: ['CigarDetail'],
    }),

    getCigarImages: builder.query<CigarImagesResponse, string>({
      query: (id) => `/cigars/${id}/images`,
      providesTags: ['CigarImages'],
    }),

    getCigarStatus: builder.query<CigarSaveStatus, string>({
      query: (id) => `/cigars/${id}/status`,
      transformResponse: (response: { success: boolean; data: CigarSaveStatus }) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'CigarStatus', id }],
    }),

    addToWishlist: builder.mutation<void, AddToWishlistRequest>({
      query: ({ cigarId }) => ({
        url: `/cigars/${cigarId}/wishlist`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { cigarId }) => [
        'Wishlist',
        { type: 'CigarStatus', id: cigarId }
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          dispatch(wishlistApi.util.invalidateTags(['Wishlist']))
        } catch { /* mutation failed */ }
      },
    }),

    addToCollection: builder.mutation<void, AddToCollectionRequest>({
      query: ({ cigarId, ...body }) => ({
        url: `/cigars/${cigarId}/collection`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { cigarId }) => [
        'Collection',
        { type: 'CigarStatus', id: cigarId }
      ],
    }),
  }),
})

export const {
  useGetCigarDetailQuery,
  useGetCigarImagesQuery,
  useGetCigarStatusQuery,
  useAddToWishlistMutation,
  useAddToCollectionMutation,
} = cigarDetailApi
